/**
 * Reports API - RTK Query service for report generation and management
 *
 * This service provides endpoints for:
 * - Generating reports (legacy and enhanced formats)
 * - Managing report templates
 * - Exporting report data
 */

import { api } from "../../api";

export const reportsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    // Generate report - supports both legacy and enhanced formats
    generateReport: builder.mutation({
      query: (reportData) => ({
        url: "/reports",
        method: "POST",
        body: reportData,
      }),
      transformResponse: (response, meta, arg) => {
        // Add metadata about the request for better caching/display
        return {
          ...response,
          requestData: arg,
          generatedAt: new Date().toISOString(),
        };
      },
      transformErrorResponse: (response, meta, arg) => {
        return {
          status: response.status,
          data: response.data,
          error: response.data?.message || "Failed to generate report",
        };
      },
      // Don't cache report results since they can be dynamic
      keepUnusedDataFor: 0,
    }),

    // Create report template
    createReportTemplate: builder.mutation({
      query: (templateData) => ({
        url: "/reports/templates",
        method: "POST",
        body: templateData,
      }),
      invalidatesTags: ["ReportTemplate"],
    }),

    // Get all report templates
    getReportTemplates: builder.query({
      query: (params = {}) => ({
        url: "/reports/templates",
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: "ReportTemplate", id: _id })),
              { type: "ReportTemplate", id: "LIST" },
            ]
          : [{ type: "ReportTemplate", id: "LIST" }],
    }),

    // Get specific report template
    getReportTemplate: builder.query({
      query: (id) => ({
        url: `/reports/templates/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: "ReportTemplate", id }],
    }),

    // Update report template
    updateReportTemplate: builder.mutation({
      query: ({ id, ...templateData }) => ({
        url: `/reports/templates/${id}`,
        method: "PUT",
        body: templateData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "ReportTemplate", id },
        { type: "ReportTemplate", id: "LIST" },
      ],
    }),

    // Delete (archive) report template
    deleteReportTemplate: builder.mutation({
      query: (id) => ({
        url: `/reports/templates/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "ReportTemplate", id },
        { type: "ReportTemplate", id: "LIST" },
      ],
    }),

    // Get report templates by category
    getReportTemplatesByCategory: builder.query({
      query: ({ category, modelName }) => ({
        url: "/reports/templates",
        params: { category, modelName },
      }),
      providesTags: ["ReportTemplate"],
    }),

    // Saved report management
    saveReport: builder.mutation({
      query: (reportData) => ({
        url: "/reports/saved",
        method: "POST",
        body: reportData,
      }),
      invalidatesTags: ["SavedReport"],
    }),

    // Get saved reports
    getSavedReports: builder.query({
      query: (params = {}) => ({
        url: "/reports/saved",
        params,
      }),
      providesTags: (result) =>
        result?.docs
          ? [
              ...result.docs.map(({ _id }) => ({
                type: "SavedReport",
                id: _id,
              })),
              { type: "SavedReport", id: "LIST" },
            ]
          : [{ type: "SavedReport", id: "LIST" }],
    }),

    // Get specific saved report
    getSavedReport: builder.query({
      query: (id) => ({
        url: `/reports/saved/${id}`,
      }),
      providesTags: (result, error, id) => [{ type: "SavedReport", id }],
    }),

    // Update saved report
    updateSavedReport: builder.mutation({
      query: ({ id, ...reportData }) => ({
        url: `/reports/saved/${id}`,
        method: "PUT",
        body: reportData,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: "SavedReport", id },
        { type: "SavedReport", id: "LIST" },
      ],
    }),

    // Delete saved report
    deleteSavedReport: builder.mutation({
      query: (id) => ({
        url: `/reports/saved/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        { type: "SavedReport", id },
        { type: "SavedReport", id: "LIST" },
      ],
    }),

    // Get storage statistics
    getStorageStats: builder.query({
      query: () => ({
        url: "/reports/stats",
      }),
      providesTags: ["StorageStats"],
    }),

    /**
     * Legs-by-Route Audit (Summary)
     * GET /reports/audit/legs-by-route/summary
     */
    getLegsByRouteAuditSummary: builder.query({
      query: (params) => ({
        url: "/reports/audit/legs-by-route/summary",
        params,
      }),
      keepUnusedDataFor: 0,
    }),

    /**
     * Legs-by-Route Audit (Details)
     * GET /reports/audit/legs-by-route/details
     */
    getLegsByRouteAuditDetails: builder.query({
      query: (params) => ({
        url: "/reports/audit/legs-by-route/details",
        params,
      }),
      keepUnusedDataFor: 0,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGenerateReportMutation,
  useCreateReportTemplateMutation,
  useGetReportTemplatesQuery,
  useGetReportTemplateQuery,
  useUpdateReportTemplateMutation,
  useDeleteReportTemplateMutation,
  useGetReportTemplatesByCategoryQuery,
  useSaveReportMutation,
  useGetSavedReportsQuery,
  useGetSavedReportQuery,
  useUpdateSavedReportMutation,
  useDeleteSavedReportMutation,
  useGetStorageStatsQuery,
  useGetLegsByRouteAuditSummaryQuery,
  useLazyGetLegsByRouteAuditDetailsQuery,
} = reportsApi;

// Backward compatibility exports
export const useGenerateDriverReportMutation = useGenerateReportMutation;
export const useGetDriverReportsQuery = useGetReportTemplatesQuery;
